import { useQuery } from '@/hooks/useQuery'
import { graphql } from 'gql.tada'
import { useRouter } from 'next/router'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { maybeGraphQLType } from '@/utils/graphql'
import { cn } from '@/utils/tw-helpers'
import { PopoverArrow, PopoverPortal } from '@radix-ui/react-popover'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'

import ArrowDownIcon from '@/assets/icons/ic_logic-g-app_arrow-down.svg'
import UserIcon from '@/assets/icons/ic_logic-g-app_portfolio-solid.svg'

interface Props {
  loginId: string
  handleSignOutClick: () => Promise<void>
}

export const TopHeaderMenu = ({
  loginId,
  handleSignOutClick,
}: Props): JSX.Element => {
  const [openMenu, setOpenMenu] = useState(false)
  const router = useRouter()

  const [{ data }] = useQuery({
    query: TopHeaderMenu_Viewer,
  })

  const viewer = maybeGraphQLType('User')(data?.currentUser)
  const schoolAdmin = maybeGraphQLType('SchoolUser')(data?.currentUser)

  return (
    <Popover open={openMenu} onOpenChange={setOpenMenu} modal={true}>
      <PopoverTrigger
        className={cn(
          'flex h-[38px] w-[62px] items-center rounded-[10px] bg-basic-bg px-1 text-[13px] font-bold text-primary-100 md:h-[44px] md:w-[192px] md:px-2.5 lg:h-[46px] lg:text-[13px]',
          openMenu && 'bg-primary-100 text-white'
        )}
        onClick={() => setOpenMenu(!openMenu)}
      >
        <UserIcon width={30} />
        <span className="ml-[2px] hidden md:block">
          {viewer
            ? `${viewer?.lastName} ${viewer?.firstName}`
            : `${schoolAdmin?.lastName} ${schoolAdmin?.firstName}`}
        </span>
        <ArrowDownIcon width={28} className="ml-auto" />
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className="border-none p-0"
          align="end"
          onEscapeKeyDown={() => setOpenMenu(false)}
        >
          <div className="w-[304px] rounded-[6px] bg-basic-bg p-[14px] shadow-[0px_2px_2px_#00000026]">
            <div className="flex gap-2 rounded-[6px] bg-white p-[10px]">
              <div className="flex flex-col justify-center overflow-hidden pl-[3px] text-xs font-bold leading-normal">
                <p className="text-primary-text">ログイン中</p>
                <p className="truncate text-[10px] text-secondary-text">
                  {loginId}
                </p>
              </div>
              <div className="ml-auto">
                <Button
                  variant="light"
                  className="h-[38px] w-[84px] items-center justify-center px-2 text-xs"
                  onClick={async () => {
                    await handleSignOutClick()
                    setOpenMenu(false)
                  }}
                >
                  ログアウト
                </Button>
              </div>
            </div>
            <div className="mt-[10px] text-center">
              <Button
                variant="secondary"
                className="h-[38px] min-w-[170px] text-xs"
                onClick={() => {
                  setOpenMenu(false)
                  router.push('/web/user-setting')
                }}
              >
                ロジックラボ 会員ページ
              </Button>
            </div>
          </div>
          <PopoverArrow className="fill-[#F8F8F8]" />
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  )
}

const TopHeaderMenu_Viewer = graphql(`
  query TopHeaderMenu_Viewer {
    currentUser {
      __typename
      ... on User {
        id
        firstName
        lastName
      }
      ... on SchoolUser {
        id
        firstName
        lastName
      }
    }
  }
`)
